import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm, SubmitHandler, useWatch } from 'react-hook-form';

import MainContainerWithWindow from '../../../../../../components/MainContainerWithWindow/MainContainerWithWindow';
import { Loading } from '../../../../../../components/Loading/Loading';
import { getRequestErrorMessage, pushNotify } from '../../../../../../components/Notifications';
import { useCreateWithdrawalMutation } from '../../../../../../redux/services/MerchantApi';
import { useGetCurrenciesQuery } from '../../../../../../redux/services/CurrencyApi';
import { ControlledInput } from '../../../../../../components/ControlledInput/ControlledInput';
import { Selector, TItem } from '../CreateReport/SelectType';
import styles from './CreateWithdrawal.module.css';
import { sanitizeCurrencyAmount, validateCurrencyAmount } from '../../../../../../utils/functions/currencyAmount';

interface WithdrawalInputs {
  amount: string;
  currency_code_to: string;
  requisite_type: 'SBP' | 'C2C' | 'IBAN' | 'TRANSFER';
  phone_number?: string;
  bank?: string;
  requisite_number?: string;
  account_owner?: string;
}

export const CreateWithdrawal: React.FC<{ open: boolean; onClose: () => void; onSuccess?: () => void }> = ({ open, onClose, onSuccess }) => {
  const methods = useForm<WithdrawalInputs>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: '',
      currency_code_to: '',
      requisite_type: 'SBP',
      phone_number: '',
      bank: '',
      requisite_number: '',
      account_owner: '',
    },
  });

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid },
  } = methods;
  const currencyCode = useWatch({ control: methods.control, name: 'currency_code_to' });
  const paymentMethod = useWatch({ control: methods.control, name: 'requisite_type' });

  const [createWithdrawal, { isLoading: isSubmitting }] = useCreateWithdrawalMutation();
  const {
    data: currenciesData,
    isLoading: isCurrencyLoading,
    isError: isCurrencyError,
    refetch: refetchCurrencies,
  } = useGetCurrenciesQuery('withdrawal', { skip: !open, refetchOnMountOrArgChange: true });
  const currencies = Array.isArray(currenciesData) ? currenciesData : [];
  const hasInvalidCurrencyData = currenciesData !== undefined && !Array.isArray(currenciesData);

  const selectedCurrency = currencies.find((currency) => currency.code === currencyCode);
  const currencyOptions: TItem<string>[] = currencies.map((currency) => ({
    id: currency.code,
    name: `${currency.code} · ${currency.name}`,
  }));
  const methodLabels: Record<string, string> = { SBP: 'СБП', C2C: 'Банковская карта', IBAN: 'IBAN', TRANSFER: 'Перевод по счёту' };
  const methodOptions: TItem<'SBP' | 'C2C' | 'IBAN' | 'TRANSFER'>[] = (selectedCurrency?.payment_methods || [])
    .filter((method): method is WithdrawalInputs['requisite_type'] => method in methodLabels)
    .map((method) => ({ id: method, name: methodLabels[method] }));

  useEffect(() => {
    if (!currencyCode && currencies[0]) {
      setValue('currency_code_to', currencies[0].code, { shouldValidate: true });
    }
  }, [currencies, currencyCode, setValue]);

  useEffect(() => {
    if (methodOptions[0]) {
      setValue('requisite_type', methodOptions[0].id, { shouldValidate: true });
    }
  }, [selectedCurrency, setValue]);

  useEffect(() => {
    setValue('phone_number', '');
    setValue('bank', '');
    setValue('requisite_number', '');
    setValue('account_owner', '');
  }, [paymentMethod, setValue]);

  const onSubmit: SubmitHandler<WithdrawalInputs> = async (data) => {
    const numericAmount = data.amount.replace(/\s/g, '');
    const amountError = validateCurrencyAmount(numericAmount, selectedCurrency);
    if (amountError) {
      pushNotify(amountError, 'Error');
      return;
    }

    const body = {
      currency_code_from: 'USDT',
      currency_code_to: data.currency_code_to,
      amount: numericAmount,
      requisite_type: data.requisite_type,
      bank: data.requisite_type !== 'IBAN' ? data.bank || '' : undefined,
      requisite_number:
        data.requisite_type === 'SBP'
          ? data.phone_number || ''
          : data.requisite_type === 'C2C'
          ? (data.requisite_number || '').replace(/\s/g, '')
          : data.requisite_type === 'IBAN'
          ? data.requisite_number || ''
          : data.requisite_number || '',
      account_owner: data.requisite_type === 'IBAN' ? data.account_owner || '' : undefined,
    };

    try {
      await createWithdrawal(body).unwrap();
      pushNotify('Заявка на вывод успешно создана', 'Success');
      onSuccess?.();
      onClose();
      reset();
    } catch (error) {
      pushNotify(getRequestErrorMessage(error, 'Ошибка при создании заявки на вывод'), 'Error');
    }
  };

  if (!open) return null;
  if (isCurrencyLoading || isSubmitting) {
    return createPortal(
      <MainContainerWithWindow>
        <Loading />
      </MainContainerWithWindow>,
      document.body
    );
  }
  if (isCurrencyError || hasInvalidCurrencyData) {
    return createPortal(
      <>
        <div className={styles.backdrop} />
        <section className={styles.modal} role="alertdialog" aria-modal="true" aria-label="Ошибка загрузки валют">
          <p>Не удалось загрузить доступные валюты.</p>
          <div className={styles.errorActions}>
            <button type="button" className={styles.secondaryButton} onClick={() => { onClose(); reset(); }}>Закрыть</button>
            <button type="button" className={styles.button} onClick={() => refetchCurrencies()}>Повторить</button>
          </div>
        </section>
      </>,
      document.body,
    );
  }

  return createPortal(
    <FormProvider {...methods}>
      <div className={styles.backdrop} onClick={() => { onClose(); reset(); }} />
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="create-withdrawal-title" onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 id="create-withdrawal-title" className={styles.title}>Новая заявка на вывод</h2>
          <button type="button" aria-label="Закрыть" className={styles.closeBtn} onClick={() => { onClose(); reset(); }}>×</button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label}>Сумма для вывода</label>
            <ControlledInput
              name="amount"
              placeholder="Введите сумму"
              rules={{ required: 'Введите сумму' }}
              transform={(text) =>
                text
                  ? sanitizeCurrencyAmount(text, selectedCurrency?.amount_scale)
                  : text
              }
              errorText={errors.amount?.message}
            />
          </div>
          <Selector
            title="Валюта"
            items={currencyOptions}
            value={currencyCode}
            onChange={(v) => setValue('currency_code_to', v, { shouldValidate: true })}
          />
          <Selector
            title="Метод оплаты"
            items={methodOptions}
            value={paymentMethod}
            onChange={(v) => setValue('requisite_type', v, { shouldValidate: true })}
          />

          {paymentMethod === 'SBP' && (
            <>
              <div className={styles.field}>
                <label className={styles.label}>Номер телефона</label>
                <ControlledInput
                  name="phone_number"
                  placeholder="+7XXXXXXXXXX"
                  rules={{ required: 'Укажите номер телефона', pattern: { value: /^\+\d{11}$/, message: 'Некорректный номер' } }}
                  errorText={errors.phone_number?.message}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Банк</label>
                <ControlledInput
                  name="bank"
                  placeholder="Название банка"
                  rules={{ required: 'Укажите банк' }}
                  errorText={errors.bank?.message}
                />
              </div>
            </>
          )}

          {paymentMethod === 'C2C' && (
            <>
              <div className={styles.field}>
                <label className={styles.label}>Номер карты</label>
                <ControlledInput
                  name="requisite_number"
                  placeholder="Номер карты"
                  rules={{ required: 'Укажите номер карты', pattern: { value: /^\d{4}\s?\d{4}\s?\d{4}\s?\d{4}$/, message: 'Некорректный номер карты' } }}
                  errorText={errors.requisite_number?.message}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Банк</label>
                <ControlledInput
                  name="bank"
                  placeholder="Название банка"
                  rules={{ required: 'Укажите банк' }}
                  errorText={errors.bank?.message}
                />
              </div>
            </>
          )}

          {paymentMethod === 'IBAN' && (
            <>
              <div className={styles.field}>
                <label className={styles.label}>Номер реквизита (IBAN)</label>
                <ControlledInput
                  name="requisite_number"
                  placeholder="Введите IBAN"
                  rules={{ required: 'Укажите IBAN' }}
                  errorText={errors.requisite_number?.message}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Владелец счёта</label>
                <ControlledInput
                  name="account_owner"
                  placeholder="ФИО владельца"
                  rules={{ required: 'Укажите владельца счёта' }}
                  errorText={errors.account_owner?.message}
                />
              </div>
            </>
          )}

          {paymentMethod === 'TRANSFER' && (
            <>
              <div className={styles.field}>
                <label className={styles.label}>Номер счёта</label>
                <ControlledInput
                  name="requisite_number"
                  placeholder="Введите номер счёта"
                  rules={{ required: 'Укажите номер счёта' }}
                  errorText={errors.requisite_number?.message}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Банк</label>
                <ControlledInput
                  name="bank"
                  placeholder="Название банка"
                  rules={{ required: 'Укажите банк' }}
                  errorText={errors.bank?.message}
                />
              </div>
            </>
          )}
        <div className={styles.actions}>
          <button
            type="submit"
            className={styles.button}
            disabled={!isValid || isSubmitting}
            style={{ width: 'auto', alignSelf: 'flex-end' }}
          >
            Вывести
          </button>
         </div>
        </form>
      </div>
    </FormProvider>,
    document.body
  );
};
